import AppLayout from "@/app/AppLayout";
import { AsyncBoundary, PageHeader } from "@/ui/patterns";
import { ReservationQueue } from "@/features/reservations/components/ReservationQueue";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";
import { NoBusinessYet } from "@/features/settings/components/NoBusinessYet";
import type { Today } from "@/domain/today/types";
import { useToday } from "./hooks/useToday";
import { useFirstSteps } from "./hooks/useFirstSteps";
import { FirstSteps } from "./components/FirstSteps";
import { ArrivalsToday } from "./components/ArrivalsToday";
import { TodaysOffers } from "./components/TodaysOffers";
import { MissingNotificationEmailNotice } from "./components/MissingNotificationEmailNotice";

/** Three empty sections say nothing a partner who has not started yet can act
 *  on, so the first steps stand in their place — but only while there is
 *  genuinely nothing in the day. */
function isDayEmpty(today: Today): boolean {
  return (
    today.awaitingAnswer.length === 0 &&
    today.arrivingToday.length === 0 &&
    today.todaysOffers.length === 0
  );
}

export default function TodayPage() {
  const {
    hasBusiness,
    business,
    isLoading: isLoadingBusiness,
    error: businessError,
    refetch: refetchBusiness,
  } = useUserBusiness();
  const { today, isLoading, error, refetch } = useToday();
  const { steps, isGuidanceNeeded } = useFirstSteps();
  const reachesNobodyByMail = business != null && business.notificationEmail === null;

  return (
    <AppLayout>
      <AsyncBoundary<boolean>
        data={hasBusiness}
        isLoading={isLoadingBusiness}
        error={businessError}
        onRetry={refetchBusiness}
      >
        {(partnerHasBusiness) =>
          partnerHasBusiness ? (
            <div className="space-y-10">
              <PageHeader title="Dnes" />
              {reachesNobodyByMail && <MissingNotificationEmailNotice />}
              <AsyncBoundary<Today>
                data={today}
                isLoading={isLoading}
                error={error}
                onRetry={() => void refetch()}
              >
                {(loaded) => (
                  <div className="space-y-10">
                    {isGuidanceNeeded && <FirstSteps steps={steps} />}
                    {!(isGuidanceNeeded && isDayEmpty(loaded)) && (
                      <>
                        <ReservationQueue
                          title={`Čeká na odpověď · ${loaded.awaitingAnswer.length}`}
                          queue={loaded.awaitingAnswer}
                        />
                        <ArrivalsToday arrivals={loaded.arrivingToday} />
                        <TodaysOffers offers={loaded.todaysOffers} />
                      </>
                    )}
                  </div>
                )}
              </AsyncBoundary>
            </div>
          ) : (
            <NoBusinessYet
              eyebrow="Vítejte v LastGO Partner"
              headline="Začneme tím, kdo jste."
              body="Než vypíšete první slevový termín, řekneme zákazníkům něco o vašem podniku — název, adresu a kde vás najdou. Zabere to dvě minuty."
            />
          )
        }
      </AsyncBoundary>
    </AppLayout>
  );
}
